import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import * as RCM from '@radix-ui/react-context-menu';
import { Check } from 'lucide-react';
import { cn } from './cn.js';
import { Kbd } from './Overlays.js';

/**
 * Right-click menu.
 *
 * Shares the overlay recipe with the dropdown menu, so a right-click and a
 * button-click produce the same object. Radix handles the parts that are
 * tedious to get right — pointer vs keyboard invocation, collision flipping,
 * focus return, and closing on scroll.
 */

export const ContextMenu = RCM.Root;
export const ContextMenuTrigger = RCM.Trigger;

export function ContextMenuContent({
  children,
  className,
  ...rest
}: ComponentPropsWithoutRef<typeof RCM.Content>) {
  return (
    <RCM.Portal>
      <RCM.Content
        collisionPadding={8}
        className={cn(
          'anim-content z-50 min-w-[13rem] overflow-hidden rounded-md border border-line-subtle',
          'bg-surface-raised p-1 shadow-lg',
          className,
        )}
        {...rest}
      >
        {children}
      </RCM.Content>
    </RCM.Portal>
  );
}

export function ContextMenuItem({
  children,
  className,
  icon,
  danger,
  shortcut,
  ...rest
}: ComponentPropsWithoutRef<typeof RCM.Item> & {
  icon?: ReactNode;
  danger?: boolean;
  shortcut?: string;
}) {
  return (
    <RCM.Item
      className={cn(
        'flex cursor-default select-none items-center gap-2.5 rounded-sm px-2 py-1.5 text-sm outline-none',
        'data-[highlighted]:bg-brand-tint data-[highlighted]:text-brand',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-40',
        danger && 'text-danger data-[highlighted]:bg-danger-tint data-[highlighted]:text-danger',
        className,
      )}
      {...rest}
    >
      {icon && (
        <span aria-hidden className="shrink-0 opacity-70 [&>svg]:h-3.5 [&>svg]:w-3.5">
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {shortcut && <Kbd className="ml-2">{shortcut}</Kbd>}
    </RCM.Item>
  );
}

export function ContextMenuLabel({ children }: { children: ReactNode }) {
  return (
    <RCM.Label className="px-2 pb-1 pt-1.5 text-3xs font-semibold uppercase tracking-[0.09em] text-ink-tertiary">
      {children}
    </RCM.Label>
  );
}

export function ContextMenuSeparator() {
  return <RCM.Separator className="my-1 h-px bg-line-subtle" />;
}

export function ContextMenuSub({ children }: { children: ReactNode }) {
  return <RCM.Sub>{children}</RCM.Sub>;
}

export function ContextMenuSubTrigger({
  children,
  icon,
}: {
  children: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <RCM.SubTrigger
      className={cn(
        'flex cursor-default select-none items-center gap-2.5 rounded-sm px-2 py-1.5 text-sm outline-none',
        'data-[highlighted]:bg-brand-tint data-[highlighted]:text-brand',
        'data-[state=open]:bg-brand-tint data-[state=open]:text-brand',
      )}
    >
      {icon && (
        <span aria-hidden className="shrink-0 opacity-70 [&>svg]:h-3.5 [&>svg]:w-3.5">
          {icon}
        </span>
      )}
      <span className="flex-1">{children}</span>
      <span aria-hidden className="text-ink-tertiary">
        ›
      </span>
    </RCM.SubTrigger>
  );
}

export function ContextMenuSubContent({ children }: { children: ReactNode }) {
  return (
    <RCM.Portal>
      <RCM.SubContent
        sideOffset={4}
        collisionPadding={8}
        className="anim-content z-50 min-w-[12rem] overflow-hidden rounded-md border border-line-subtle bg-surface-raised p-1 shadow-lg"
      >
        {children}
      </RCM.SubContent>
    </RCM.Portal>
  );
}

export function ContextMenuCheckItem({
  children,
  checked,
  onSelect,
}: {
  children: ReactNode;
  checked: boolean;
  onSelect: () => void;
}) {
  return (
    <RCM.Item
      onSelect={onSelect}
      className={cn(
        'relative flex cursor-default select-none items-center gap-2 rounded-sm py-1.5 pl-7 pr-2 text-sm outline-none',
        'data-[highlighted]:bg-brand-tint data-[highlighted]:text-brand',
      )}
    >
      {checked && <Check aria-hidden className="absolute left-2 h-3.5 w-3.5 text-brand" />}
      {children}
    </RCM.Item>
  );
}
